import { Button, Grid } from "@mui/material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import styles from "../../../../assets/styles/componentStyles/chatbotStyles/chatbotIntegration/chatbotIntegrationPopup.module.scss";

import ConfigurationPopup from "../configurationPopup/ConfigurationPopup";
import ConfigurationHeader from "../configurationPopup/ConfigurationHeader";
import InstagramAutomationTabData from "./InstagramAutomationTabData";
import GlobalButton from "../../../utility/globalButton/button";

import instagramIcon from '../../../../assets/images/channels/instagram/instagram.png';
import { useContext, useEffect, useState } from "react";
import agent from "../../../../services/api";
import { UserContext, UserContextPayload } from "../../../../context/userContext";
import { instagramCommentTriggerPayload } from "../../../../models/axiosModel";
import { useGenericStore } from "../../../../store/genericStore";
import { useShallow } from "zustand/react/shallow";
import GlobalPopUp from "../../../utility/globalPopUp";
import GlobalDeletePopup from "../../../utility/globalDeletePopup/globalDeletePopup";

interface InstagramConfigurationProps {
    open: boolean;
    page: string;
    pageID: string;
    handleClose: () => void;
    channelDeploymentID: string;
}

const InstagramConfiguration = (props: InstagramConfigurationProps) => {

    const { open, page, pageID, handleClose, channelDeploymentID } = props;

    const { setFlagLoading, flagLoading, handleError } = useContext(UserContext) as UserContextPayload;

    const { showSuccess, showError } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess,
        showError: state.showError
    })))

    const [automationData, setAutomationData] = useState<instagramCommentTriggerPayload | null>(null);
    const [showDeleteConfirmationPopup, setShowDeleteConfirmationPopup] = useState(false);

    // Constants
    const channelDeploymentTriggerID = automationData?.chatbot_integrated_channel_deployment_trigger_id;
    const isSaveDisabled = !(automationData?.condition_group?.condition_values?.length);

    const instagramTabs = [
        {
            id: "automation",
            label: "Automation",
            icon: <AutoAwesomeIcon />,
            component: 
                <InstagramAutomationTabData 
                    automationData={automationData}
                    setAutomationData={setAutomationData}
                />
        }
    ];

    // API Calls
    const getInstagramCommentTriggerApi = async () => {
        setFlagLoading({...flagLoading, flagRoundLoader: true});
        
        const res = await agent.getInstagramCommentTrigger.get(channelDeploymentID);

        const data = Array.isArray(res.data) ? res.data[0] : res.data as instagramCommentTriggerPayload;
        
        if (res.status) {
            setAutomationData(data ?? {
                chatbot_integrated_channel_deployment_id: channelDeploymentID,
                comment_reply: null,
                condition_group: {
                    flag_advanced_condition: false,
                    condition_type: "static_value_check",
                    condition_values: [],
                },
            });
        } else {
            handleError(res);
        }

        setFlagLoading({...flagLoading, flagRoundLoader: false});
    }

    const createInstagramCommentTriggerApi = async () => {
        
        setFlagLoading({...flagLoading, flagRoundLoader: true});

        const payload = {
            chatbot_integrated_channel_deployment_id: channelDeploymentID,
            comment_reply: automationData?.comment_reply,
            condition_group: {
                condition_group_id: undefined,
                flag_advanced_condition: false,
                condition_type: "static_value_check",
                condition_values: automationData?.condition_group?.condition_values,
            }
        } as instagramCommentTriggerPayload;

        const res = await agent.createInstagramCommentTrigger.post(payload);
        
        if (res.status) {
            showSuccess("Instagram Comment Trigger Automation created successfully!");
        } else {
            handleError(res);
        }

        handleClose();
        setFlagLoading({...flagLoading, flagRoundLoader: false});
    }

    const updateInstagramCommentTriggerApi = async () => {
        
        setFlagLoading({...flagLoading, flagRoundLoader: true});
        
        const payload = {
            chatbot_integrated_channel_deployment_trigger_id: automationData?.chatbot_integrated_channel_deployment_trigger_id,
            comment_reply: automationData?.comment_reply,
            condition_group: automationData?.condition_group
        } as instagramCommentTriggerPayload;

        const res = await agent.updateInstagramCommentTrigger.put(payload);

        if (res.status) {
            showSuccess("Instagram Comment Trigger Automation updated successfully!");
        } else {
            handleError(res);
        }

        handleClose();
        setFlagLoading({...flagLoading, flagRoundLoader: false});
    }

    const deleteInstagramCommentTriggerApi = async () => {
        if (!channelDeploymentTriggerID) return;
        
        setFlagLoading({...flagLoading, flagRoundLoader: true});
        
        const res = await agent.deleteInstagramCommentTrigger.delete(channelDeploymentTriggerID);
        
        if (res.status) {
            showSuccess("Instagram Comment Trigger Automation deleted successfully!");
        } else {
            handleError(res);
        }

        setShowDeleteConfirmationPopup(false);
        handleClose();
        setFlagLoading({...flagLoading, flagRoundLoader: false});
    }

    // Handle Save
    const handleSave = async () => {
        if (channelDeploymentTriggerID) {
            await updateInstagramCommentTriggerApi();
        } else {
            await createInstagramCommentTriggerApi();
        }
    };

    // Render Functions
    const renderSaveAndCancelButtons = () => {
        return (
            <>
                <Grid item>
                    <GlobalButton
                        buttonText="Cancel"
                        className="borderedButtonStyle"
                        onClick={handleClose}
                    />
                </Grid>

                <Grid item>
                    <GlobalButton
                        buttonText="Save Changes"
                        className="primaryButtonStyle"
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                    />
                </Grid>
            </>
        )
    }

    // useEffect
    useEffect(() => {
        getInstagramCommentTriggerApi();
    }, []);

    return (
        <>
            <ConfigurationPopup
                open={open}
                onClose={handleClose}
                title="Configure Instagram Account"
                header={
                    <ConfigurationHeader
                        title={page}
                        subTitle={pageID}
                        subTitleLabel="ID"
                        icon={instagramIcon}
                    />
                }
                tabs={instagramTabs}
                footer={
                    <Grid item xs={12} className={styles.configurationFooterActions}>
                        {channelDeploymentTriggerID ? (
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <Button
                                        type="button"
                                        variant="text"
                                        disableRipple
                                        className={styles.deleteIntegrationButton}
                                        onClick={() => setShowDeleteConfirmationPopup(true)}
                                    >
                                        Delete Integration
                                    </Button>
                                </Grid>

                                <Grid item>
                                    <Grid container gap={1}>
                                        {renderSaveAndCancelButtons()}
                                    </Grid>
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid container justifyContent="center" gap={2}>
                                {renderSaveAndCancelButtons()}
                            </Grid>
                        )}
                    </Grid>
                }
            />

            {showDeleteConfirmationPopup &&
                <GlobalPopUp
                    open={showDeleteConfirmationPopup}
                    handleClose={() => { setShowDeleteConfirmationPopup(false) }}
                >
                    <GlobalDeletePopup
                        deleteDescription={`Are you sure you want to delete this Instagram Automation`}
                        onClose={() => { setShowDeleteConfirmationPopup(false) }}
                        onDelete={deleteInstagramCommentTriggerApi}
                    />
                </GlobalPopUp>
            }

        </>
    );
};

export default InstagramConfiguration;